<script setup lang="ts">
import { ref, reactive, computed, nextTick } from 'vue'
import useProductSharing from '@/legacy/ts/useProductSharing'
import {
	kFormatter,
	getProductSharingUrl,
	productSharingData,
	productSharingPostPerPage,
	ProductSharingImageSize,
	MemberTypes,
} from '@/legacy/ts/productSharing'
import BsCollapse from '@/legacy/components/Collapse.vue'
import BsDialogFull from '@/legacy/components/DialogFull.vue'
import BsSideDrawer from '@/legacy/components/SideDrawer.vue'
import BsProductSharingMessageCard from '@/legacy/components/ProductSharingMessageCard.vue'
import BsProductSharingFollowButton from '@/legacy/components/ProductSharingFollowButton.vue'
import BsProductSharingUserContribution from '@/legacy/components/ProductSharingUserContribution.vue'
import {
	fetchInappUserBadges,
	instanceProductSharing,
} from '@/assets/ts/apiManagement'
import { getVueAssetImagePath } from '@/lib/utils'
import BaseIcon from '@/components/ui/icon/BaseIcon.vue'
import {
	type IProductSharingMemberInfo,
	type IContributionInfo,
} from '@/types/ProductSharing'

const { getPSUsername, connectPS } = useProductSharing()

const props = defineProps({
	isPrivate: {
		type: Boolean,
		default: false,
	},
	userProfile: {
		type: Object as () => IProductSharingMemberInfo,
		require: true,
	},
	text: {
		type: Object,
	},
})

const collapseHeight = ref(20)
const isLoading = ref(false)
const isShowMemberTypeDialog = ref(false)
const isShowPanelFollowers = ref(false)
const isShowPanelFollowing = ref(false)
const isShowPanelContribution = ref(false)
const contributionInfo = ref<IContributionInfo | undefined>(undefined)
const followersList = reactive<IProductSharingMemberInfo[]>([])
const followingList = reactive<IProductSharingMemberInfo[]>([])
const isDesktop = ref(window.matchMedia('(min-width: 768px)').matches)
const selfProfileName = ref<string | undefined>(undefined)
const memberDialogContentMap = ref<Awaited<
	ReturnType<typeof fetchInappUserBadges>
> | null>(null)
const page = reactive({
	follower: 1,
	following: 1,
})
const isLoadingFollowers = ref(true)
const isLoadingFollowing = ref(true)
const isLoadingMoreFollowers = ref(false)
const isLoadingMoreFollowing = ref(false)
const isNoMoreFollowers = ref(false)
const isNoMoreFollowing = ref(false)
const perPage = productSharingPostPerPage.follow
const refFollowersList = ref<Element | null>(null)
const refFollowingList = ref<Element | null>(null)
const refObserverFollower = ref<Element | null>(null)
const refObserverFollowing = ref<Element | null>(null)

const isFollowersEmpty = computed(() => props.userProfile?.followerCount === 0)
const isFollowingEmpty = computed(() => props.userProfile?.followingCount === 0)
const displayContributionsTotal = computed(() => {
	if (props.userProfile?.totalContributions) {
		return kFormatter(props.userProfile.totalContributions, props.text)
	}
	return 0
})
const displayFollowerTotal = computed(() => {
	if (props.userProfile?.followerCount) {
		return kFormatter(props.userProfile.followerCount, props.text)
	}
	return 0
})
const displayFollowingTotal = computed(() => {
	if (props.userProfile?.followingCount) {
		return kFormatter(props.userProfile.followingCount, props.text)
	}
	return 0
})
const isMemberOfficial1 = computed(() => {
	if (props.userProfile?.memberTypes.length) {
		return props.userProfile.memberTypes.includes(MemberTypes.Official1)
	}
	return false
})
const isMemberAssigned1 = computed(() => {
	if (props.userProfile?.memberTypes.length) {
		return props.userProfile.memberTypes.includes(MemberTypes.Assigned1)
	}
	return false
})
const isMemberAssigned2 = computed(() => {
	if (props.userProfile?.memberTypes.length) {
		return props.userProfile.memberTypes.includes(MemberTypes.Assigned2)
	}
	return false
})
const isShowMemberBadges = computed(() => {
	return (
		isMemberOfficial1.value ||
		isMemberAssigned1.value ||
		isMemberAssigned2.value
	)
})
const memberTitle = computed<string>(() => {
	if (isMemberOfficial1.value) {
		return props.text?.memberTypes.official1
	}
	if (isMemberAssigned1.value) {
		return props.text?.memberTypes.assigned1
	}
	if (isMemberAssigned2.value) {
		return props.text?.memberTypes.assigned2
	}
	return ''
})
const currentMemberType = computed(() => {
	if (props.userProfile && props.userProfile.memberTypes.length) {
		return props.userProfile.memberTypes[0]
	}
	return null
})
const currentMemberDialogContent = computed(() => {
	if (
		!memberDialogContentMap.value ||
		!currentMemberType.value ||
		!memberDialogContentMap.value[currentMemberType.value]
	)
		return null
	return {
		title: memberDialogContentMap.value[currentMemberType.value].title,
		content: memberDialogContentMap.value[currentMemberType.value].content,
	}
})
const decodeUserName = computed(() => {
	return props.userProfile?.username
		? decodeURIComponent(props.userProfile.username)
		: ''
})

async function getSelfProfileName() {
	if (selfProfileName.value !== undefined) {
		return selfProfileName.value
	}
	const PSUsername = await getPSUsername()
	selfProfileName.value = PSUsername
	return PSUsername
}

function openPanelFollowers() {
	if (isFollowersEmpty.value) return
	if (!productSharingData.isLogin) {
		window.BsHeaderVm.showPanel('login')
		return
	}
	isShowPanelFollowers.value = true
	getSelfProfileName().then(() => {
		fetchFollowers()
		nextTick(() => {
			initIntersectionObserver('follower')
		})
	})
}

function fetchFollowers() {
	const start = page.follower > 1 ? (page.follower - 1) * perPage + 1 : 1
	instanceProductSharing
		.get(
			`/users/${props.userProfile?.username}/followers/?limit=${perPage}&start=${start}`,
		)
		.then(({ data: { data } }) => {
			const followerListRes: IProductSharingMemberInfo[] = data.map(
				(item: any) => ({
					description: item.description,
					image: item.image,
					site: item.site,
					username: item.username,
					followingCount: item.following_count,
					followerCount: item.follower_count,
					likeCount: item.like_count,
					bookmarkCount: item.bookmark_count,
					totalContributions: item.total_contributions,
					isFollowed: item.is_followed,
					latestPostsImages: item.latest_posts_images,
				}),
			)

			if (followerListRes.length === 0) {
				isNoMoreFollowers.value = true
				isLoadingMoreFollowers.value = false
			} else {
				const followerMap = new Map<string, IProductSharingMemberInfo>()
				const temp = [...followersList, ...followerListRes]
				temp.forEach(item => {
					followerMap.set(item.username, item)
				})

				const uniqueFollowers = Array.from(followerMap.values())

				followersList.splice(0, followersList.length, ...uniqueFollowers)
			}

			if (start === 1) isLoadingFollowers.value = false
		})
		.catch(err => {
			if (err.response && err.response.status === 401) {
				connectPS().then(() => fetchFollowers())
			}
		})
}

function openPanelFollowing() {
	if (isFollowingEmpty.value) return
	if (!productSharingData.isLogin) {
		window.BsHeaderVm.showPanel('login')
		return
	}
	isShowPanelFollowing.value = true
	getSelfProfileName().then(() => {
		fetchFollowing()
		nextTick(() => {
			initIntersectionObserver('following')
		})
	})
}

function fetchFollowing() {
	const start = page.following > 1 ? (page.following - 1) * perPage + 1 : 1
	instanceProductSharing
		.get(
			`/users/${props.userProfile?.username}/following/?limit=${perPage}&start=${start}`,
		)
		.then(({ data: { data } }) => {
			const followingListRes: IProductSharingMemberInfo[] = data.map(
				(item: any) => ({
					description: item.description,
					image: item.image,
					site: item.site,
					username: item.username,
					followingCount: item.following_count,
					followerCount: item.follower_count,
					likeCount: item.like_count,
					bookmarkCount: item.bookmark_count,
					totalContributions: item.total_contributions,
					isFollowed: item.is_followed,
					latestPostsImages: item.latest_posts_images,
				}),
			)

			if (followingListRes.length === 0) {
				isNoMoreFollowing.value = true
				isLoadingMoreFollowing.value = false
			} else {
				const followingMap = new Map<string, IProductSharingMemberInfo>()
				const temp =
					start > 1
						? [...followingList, ...followingListRes]
						: [...followingListRes]
				temp.forEach(item => {
					followingMap.set(item.username, item)
				})

				const uniqueFollowers = Array.from(followingMap.values())

				followingList.splice(0, followingList.length, ...uniqueFollowers)
			}

			if (start === 1) isLoadingFollowing.value = false
		})
		.catch(err => {
			if (err.response && err.response.status === 401) {
				connectPS().then(() => fetchFollowing())
			}
		})
}

function openPanelContributions() {
	if (!productSharingData.isLogin) {
		window.BsHeaderVm.showPanel('login')
		return
	}

	if (selfProfileName.value) {
		fetchContributions()
	} else {
		getSelfProfileName().then(() => {
			if (!selfProfileName.value) {
				window.location.href = getProductSharingUrl('private-index')
			}
			fetchContributions()
		})
	}
}

function fetchContributions() {
	instanceProductSharing
		.get(`/users/${props.userProfile?.username}/contributions`)
		.then(({ data: { data } }) => {
			contributionInfo.value = {
				liked: data.liked_contributions,
				bookmarked: data.bookmarked_contributions,
				publish: data.publish_contributions,
				welcome: data.welcome_contributions,
				event: data.event_contributions,
				total: data.total_contributions,
				transformed: data.transformed_contributions,
				remaining: data.remaining_contributions,
				convert: getConvertPercent(
					data.transformed_contributions,
					data.total_contributions,
				),
			}
			isShowPanelContribution.value = true
		})
		.catch(err => {
			if (err.response && err.response.status === 401) {
				connectPS().then(() => fetchContributions())
			}
		})
}

function updateFollowStatus(payload: { name: string; val: boolean }) {
	const indexInFollower = followersList.findIndex(
		item => item.username === payload.name,
	)
	const indexInFollowing = followingList.findIndex(
		item => item.username === payload.name,
	)
	if (indexInFollower !== -1)
		followersList[indexInFollower].isFollowed = payload.val
	if (indexInFollowing !== -1)
		followingList[indexInFollowing].isFollowed = payload.val
}

function getConvertPercent(transformed: number, total: number) {
	return ((transformed / total) * 100).toFixed(2)
}

function loadMore(type: 'follower' | 'following') {
	if (type === 'follower') {
		if (props.userProfile?.followerCount === followersList.length) {
			isLoadingMoreFollowers.value = false
			return
		}
		isLoadingMoreFollowers.value = true
		page.follower += 1
		fetchFollowers()
	} else if (type === 'following') {
		if (props.userProfile?.followingCount === followingList.length) {
			isLoadingMoreFollowing.value = false
			return
		}
		isLoadingMoreFollowing.value = true
		page.following += 1
		fetchFollowing()
	}
}

function initIntersectionObserver(type: 'follower' | 'following') {
	const oberverRoot =
		type === 'follower' ? refFollowersList.value : refFollowingList.value
	const options = {
		oberverRoot,
		threshold: 0,
	}
	const callback: IntersectionObserverCallback = entries => {
		entries.forEach(entry => {
			if (
				entry.intersectionRatio > 0 &&
				type === 'follower' &&
				!isNoMoreFollowers.value
			) {
				loadMore(type)
			}
			if (
				entry.intersectionRatio > 0 &&
				type === 'following' &&
				!isNoMoreFollowing.value
			) {
				loadMore(type)
			}
		})
	}
	const observer = new IntersectionObserver(callback, options)
	const loadingObserver =
		type === 'follower' ? refObserverFollower.value : refObserverFollowing.value
	if (loadingObserver) {
		observer.observe(loadingObserver)
	}
}

function getPosterImage(userImageObj: any) {
	if (
		!userImageObj ||
		!Object.keys(userImageObj).length ||
		!userImageObj.content
	)
		return null
	if (!userImageObj.has_resize) return userImageObj.content
	return userImageObj[ProductSharingImageSize.ProfileMain]
}

function showMemberTypeDialog() {
	if (!currentMemberDialogContent.value) return
	isShowMemberTypeDialog.value = true
}

function hideMemberTypeDialog() {
	isShowMemberTypeDialog.value = false
}

async function init() {
	memberDialogContentMap.value = await fetchInappUserBadges()
}

init()
</script>

<template>
	<div class="bs-product-sharing-profile" v-if="!isLoading" v-cloak>
		<div class="bs-product-sharing-profile__top__bar"></div>
		<div class="bs-product-sharing-profile__avatar__wrapper">
			<img
				v-if="userProfile && userProfile.image"
				class="bs-product-sharing-profile__avatar"
				alt="Avatar"
				:src="getPosterImage(userProfile.image)"
			/>
			<img
				v-else
				class="bs-product-sharing-profile__avatar"
				alt="Avatar"
				:src="`${getVueAssetImagePath('avatar_2_48px@2x.png')}`"
			/>
		</div>
		<div class="bs-product-sharing-profile__info" v-if="userProfile">
			<h6
				class="bs-product-sharing-profile__info__name"
				:class="{ 'extra-space': !isShowMemberBadges }"
			>
				{{ decodeUserName }}
			</h6>
			<div
				class="bs-product-sharing-profile__info__member"
				v-if="isShowMemberBadges"
				@click="showMemberTypeDialog"
			>
				<BaseIcon
					v-if="isMemberOfficial1"
					name="member_official_1"
					class="inline-block h-12px w-12px"
				></BaseIcon>
				<BaseIcon
					v-if="isMemberAssigned1"
					name="member_assigned_1"
					class="inline-block h-12px w-12px text-o-500"
				></BaseIcon>
				<BaseIcon
					v-if="isMemberAssigned2"
					name="member_assigned_2"
					class="inline-block h-12px w-12px"
				></BaseIcon>
				<div
					class="bs-product-sharing-profile__info__member__title"
					v-if="memberTitle"
				>
					{{ memberTitle }}
				</div>
			</div>
			<div>
				<BsCollapse
					class="bs-product-sharing-profile__see_more_btn"
					:defaultHeight="collapseHeight"
					:buttonMoreText="text?.button.more"
					:buttonLessText="text?.button.less"
				>
					<template v-slot:content>
						<div>{{ userProfile.description }}</div>
					</template>
				</BsCollapse>
			</div>
		</div>
		<div class="bs-product-sharing-profile__data" v-if="userProfile">
			<div class="bs-product-sharing-profile__data__count">
				<div
					@click="openPanelContributions"
					class="bs-product-sharing-profile__data__count__item"
				>
					<div class="bs-product-sharing-profile__data__count__item__number">
						{{ displayContributionsTotal }}
					</div>
					<div class="bs-product-sharing-profile__data__count__item__text">
						{{ text?.contributions }}
					</div>
				</div>
				<div
					@click="openPanelFollowers"
					class="bs-product-sharing-profile__data__count__item"
				>
					<div class="bs-product-sharing-profile__data__count__item__number">
						{{ displayFollowerTotal }}
					</div>
					<div class="bs-product-sharing-profile__data__count__item__text">
						{{ text?.followers }}
					</div>
				</div>
				<div
					@click="openPanelFollowing"
					class="bs-product-sharing-profile__data__count__item"
				>
					<div class="bs-product-sharing-profile__data__count__item__number">
						{{ displayFollowingTotal }}
					</div>
					<div class="bs-product-sharing-profile__data__count__item__text">
						{{ text?.following }}
					</div>
				</div>
				<div
					class="bs-product-sharing-profile__data__count__item"
					:class="{
						'bs-product-sharing-profile__data__count__item__edit': isPrivate,
					}"
				>
					<!-- No need to show edit in public profile -->
					<!-- <bs-button
            v-if="isPrivate"
            :outline="true"
            type="primary"
            @click="editProfile"
          >
            {{ text.edit }}
          </bs-button> -->
					<template v-if="userProfile && !isPrivate && isDesktop">
						<BsProductSharingFollowButton
							:name="userProfile.username"
							:text="text"
							:isFollowed="userProfile.isFollowed"
							:class="{
								'bs-product-sharing-follow-button__following':
									userProfile.isFollowed,
							}"
						/>
					</template>
				</div>
			</div>
			<div
				class="bs-product-sharing-profile__data__follow"
				v-if="userProfile && !isPrivate && !isDesktop"
			>
				<BsProductSharingFollowButton
					:name="userProfile.username"
					:text="text"
					:isFollowed="userProfile.isFollowed"
					:isWidthFull="true"
				/>
			</div>
		</div>

		<Teleport to="body" v-if="userProfile">
			<div
				ref="refFollowersList"
				class="bs-product-sharing-profile__panel__follow"
			>
				<BsSideDrawer
					:title="text?.panel.follower"
					v-model="isShowPanelFollowers"
					:isShowTrailing="true"
				>
					<template v-if="isLoadingFollowers">
						<BsProductSharingMessageCard
							v-for="length in perPage"
							:key="`loading_first_follower_${length}`"
							:isLoading="true"
							:text="text"
						></BsProductSharingMessageCard>
					</template>
					<template v-else>
						<BsProductSharingMessageCard
							v-for="item in followersList"
							:userInfo="item"
							:key="item.username"
							:selfProfileName="selfProfileName"
							:text="text"
							@update="updateFollowStatus"
						></BsProductSharingMessageCard>
						<template v-if="isLoadingMoreFollowers">
							<BsProductSharingMessageCard
								v-for="length in 5"
								:key="`loading_follower_${length}`"
								:isLoading="true"
								:text="text"
							></BsProductSharingMessageCard>
						</template>
					</template>
					<div class="bs-view-more-wrapper" ref="refObserverFollower"></div>
				</BsSideDrawer>
			</div>
		</Teleport>

		<Teleport to="body" v-if="userProfile">
			<div
				ref="refFollowingList"
				class="bs-product-sharing-profile__panel__follow"
			>
				<BsSideDrawer
					:title="text?.panel.following"
					v-model="isShowPanelFollowing"
					:isShowTrailing="true"
				>
					<template v-if="isLoadingFollowing">
						<BsProductSharingMessageCard
							v-for="length in perPage"
							:key="`loading_first_following_${length}`"
							:isLoading="true"
							:text="text"
						></BsProductSharingMessageCard>
					</template>
					<template v-else>
						<BsProductSharingMessageCard
							v-for="item in followingList"
							:userInfo="item"
							:key="item.username"
							:selfProfileName="selfProfileName"
							:text="text"
							@update="updateFollowStatus"
						></BsProductSharingMessageCard>
						<template v-if="isLoadingMoreFollowing">
							<BsProductSharingMessageCard
								v-for="length in 5"
								:key="`loading_following_${length}`"
								:isLoading="true"
								:text="text"
							></BsProductSharingMessageCard>
						</template>
					</template>
					<div class="bs-view-more-wrapper" ref="refObserverFollowing"></div>
				</BsSideDrawer>
			</div>
		</Teleport>

		<Teleport to="body" v-if="userProfile">
			<div
				v-show="contributionInfo"
				class="bs-product-sharing-profile__panel__contribution"
			>
				<BsSideDrawer
					v-model="isShowPanelContribution"
					:title="text?.panel.contribution"
					:isShowTrailing="true"
				>
					<BsProductSharingUserContribution
						:info="contributionInfo"
						:text="text"
					></BsProductSharingUserContribution>
				</BsSideDrawer>
			</div>
		</Teleport>
		<Teleport to="body" v-if="userProfile && currentMemberDialogContent">
			<BsDialogFull
				class="bs-product-sharing-profile__dialog"
				v-model="isShowMemberTypeDialog"
				:isModalCenter="true"
				@on-hide="hideMemberTypeDialog"
			>
				<template #content>
					<div class="bs-product-sharing-profile__dialog">
						<div class="bs-product-sharing-profile__dialog-title">
							<div class="bs-product-sharing-profile__dialog-title-icon">
								<BaseIcon
									v-if="isMemberOfficial1"
									name="member_official_1"
									class="inline-block h-32px w-32px"
								></BaseIcon>
								<BaseIcon
									v-if="isMemberAssigned1"
									name="member_assigned_1"
									class="inline-block h-32px w-32px fill-o-500"
								></BaseIcon>
								<BaseIcon
									v-if="isMemberAssigned2"
									name="member_assigned_2"
									class="inline-block h-32px w-32px"
								></BaseIcon>
							</div>
							<div class="bs-product-sharing-profile__dialog-title-text">
								{{ currentMemberDialogContent.title }}
							</div>
						</div>
						<div class="bs-product-sharing-profile__dialog-content">
							{{ currentMemberDialogContent.content }}
						</div>
					</div>
				</template>
			</BsDialogFull>
		</Teleport>
	</div>
</template>
